import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import {
  LayoutDashboard,
  Car,
  Users,
  Receipt,
  DollarSign,
  Wrench,
  FileCheck,
  Building2,
  UserCog,
  Bell,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronRight,
  CalendarDays,
  FileInput
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Vehicles', href: '/vehicles', icon: Car },
  { name: 'Drivers', href: '/drivers', icon: Users },
  { name: 'Daily Records', href: '/daily-records', icon: CalendarDays },
  { name: 'Expenses', href: '/expenses', icon: Receipt },
  { name: 'Revenue', href: '/revenue', icon: DollarSign },
  { name: 'Invoices', href: '/invoices', icon: FileInput },
  { name: 'Maintenance', href: '/maintenance', icon: Wrench },
  { name: 'Compliance', href: '/compliance', icon: FileCheck },
  { name: 'High Profile Customers', href: '/customers', icon: Building2 },
  { name: 'Staff', href: '/staff', icon: UserCog },
  { name: 'Reports', href: '/reports', icon: FileText },
  { name: 'Alerts', href: '/alerts', icon: Bell },
];

export const Layout = ({ children, title, subtitle, actions }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isDashboard = location.pathname === '/';

  return (
    <div className={`min-h-screen ${isDashboard ? 'bg-[#09090B]' : 'bg-slate-50'}`}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-[#09090B] border-r border-zinc-800
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-zinc-800">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_fleetvision-3/artifacts/x8ibh464_image.png" 
                alt="Raftaar" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Raftaar
              </span>
            </Link>
            <button 
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                      transition-colors duration-200
                      ${isActive 
                        ? 'bg-zinc-800 text-[#D4AF37]' 
                        : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                      }
                    `}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.name}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center gap-3 px-3 py-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <span className="text-black font-bold text-sm">
                  {user?.username?.charAt(0) || 'R'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.username || 'Raftaar'}
                </p>
                <p className="text-xs text-zinc-500">Administrator</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className={`
          sticky top-0 z-30 h-16 flex items-center justify-between px-4 lg:px-8
          border-b backdrop-blur-sm
          ${isDashboard 
            ? 'bg-[#09090B]/80 border-zinc-800' 
            : 'bg-white/80 border-slate-200'
          }
        `}>
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-zinc-800"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className={`w-5 h-5 ${isDashboard ? 'text-white' : 'text-slate-900'}`} />
            </button>
            <div>
              <h1 className={`text-lg lg:text-xl font-semibold ${isDashboard ? 'text-white' : 'text-slate-900'}`} 
                  style={{ fontFamily: 'Playfair Display, serif' }}>
                {title}
              </h1>
              {subtitle && (
                <p className={`text-sm ${isDashboard ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {actions && <div className="flex items-center gap-3">{actions}</div>}
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
